import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { LAYER_TYPES } from '../../components/project-settings/types.ts';
import { LAYER_NORM_CUSTOM, LAYER_NORM_NONE } from '../../constants/layers.ts';
import { DownloadProgress, FileType } from '../../types/file.ts';
import { Lamp, LampSeed, createLampFromSeed } from '../../types/lamp.ts';
import {
  LampWireframeTamanEntry,
  LampWireframeThirdPartyEntry,
  LampWireframesState,
  initialLampWireframesState,
} from '../../types/lampWireframes.ts';
import { MeshGroup } from '../../types/mesh.ts';
import { LayerType, ModelFile, ModelLayer } from '../../types/model.ts';
import { LampSimilarityDetail, Project, Version } from '../../types/project.ts';
import { MeasurementVisualizationType, ViewModes } from '../../types/view.ts';
import { removeThirdPartyWireframeForLamp, removeThirdPartyWireframesForLamps } from '../../util/lampWireframes.ts';
import { MarketMatchPrefilterRequest } from '../../utils/marketMatchPrefilters.ts';
import { DEFAULT_MAX_APEX_ANGLE_DEGREES, clampMaxApexAngle } from '../../utils/maxApexAngle.ts';
import type { MarketMatchPreviewMode } from '../../utils/measurementCalculation/measurementSource.ts';
import { LampHistoryState, initialLampHistoryState } from '../lampHistory.ts';

interface WorkspaceState {
  showGrid: boolean;
  showCompass: boolean;
  showReferenceImage: boolean;
  efficacy: number;
  maintenanceFactor: number;
  satisfaction: number;
  /** Measurement-grid spacing in metres for the frontend measurement calculation. */
  measurementGridSpacing: number;
  /** Frontend-computed measurement points (kept separate from the ml result on the version). */
  frontendMeasurementData: MeasurementData[] | null;
  /** Which result the measurement view renders: 'ml' (backend) or 'frontend'. */
  measurementSource: 'ml' | 'frontend';
  /** True while the frontend recompute is running (drives the loading overlay). */
  frontendMeasurementCalculating: boolean;
  showIESModels: boolean;
  showMeasurementPoints: boolean;
  showNearestLampDistancesWhileMoving: boolean;
  showXYZLines: boolean;
  xLines: boolean;
  yLines: boolean;
  zLines: boolean;
  activeViewModes: ViewModes;
  useOriginalTextures: boolean;
  showMeasurements: boolean;
  hasCustomIesProfile: boolean;
  customIesProfileUrl: string | null;
  selectedObject: string | null;
  transformMode: 'translate' | 'rotate' | 'scale';
  sidebarOpen: boolean;
  hoveredLayer: string | null;
  selectedLayer: string | null;
  isLampCreationMode: boolean;
  lamps: Lamp[];
  uploadedModels: ModelFile[];
  modelLayers: ModelLayer[];
  meshGroups: MeshGroup[];
  expandedSections: { [key: string]: boolean };
  project: Project | null;
  isOrthographicView: boolean;
  cameraInitialized: boolean;
  desiredLuminaireHeight: number;
  /** Max apex angle (deg) sent with the next optimization. 180 skips the angle gate. */
  maxApexAngleDegrees: number;
  fileDownloadProgress: DownloadProgress[];
  isLoadingFiles: boolean;
  measurementVisualizationType: MeasurementVisualizationType | null;
  /**
   * Active legend filter (in measurement value units). When set, only points
   * whose value falls within `[min, max]` participate in the visualization
   * (both 3D dots and the surface heatmap).
   *
   * Each edge is independently nullable:
   *   - `min: null` ⇒ "no lower bound" (lower handle was never moved off the
   *     scale start). Values below an `max` constraint still pass.
   *   - `max: null` ⇒ "no upper bound" (upper handle was never moved off the
   *     scale end). Values above a `min` constraint still pass.
   *
   * The whole property goes back to `null` once *both* edges return to
   * "untouched", i.e. when the filter has no effect.
   *
   * Automatically cleared whenever the visualization type changes since the
   * value units differ between modes.
   */
  measurementFilterRange: { min: number | null; max: number | null } | null;
  showAutoPositionSidebar: boolean;
  // Measurement tool state
  isMeasurementMode: boolean;
  measurementPoints: [number, number, number][];
  measuredDistance: number | null;
  segmentDistances: number[];
  activeToolbarTool: 'selection' | 'target' | 'measurement' | 'lampDistance' | null;
  measurementHoverPoint: [number, number, number] | null;
  // Lamp-to-Lamp distance measurement state
  isLampDistanceMode: boolean;
  lampDistanceSelectedLamps: string[]; // Array of lamp IDs (max 2)
  lampDistancePoints: [number, number, number][]; // Coordinates of selected lamps
  lampDistanceMeasured: number | null;
  // Target camera position for programmatic camera movement
  targetCameraPosition: [number, number, number] | null;
  targetCameraLookAt: [number, number, number] | null;
  // Undo/Redo history state
  lampHistory: LampHistoryState;
  // Lamp similarity details (fetched when market matches section expands)
  lampSimilarityDetails: LampSimilarityDetail[];
  isLoadingLampSimilarityDetails: boolean;
  // Applied lamp similarity measurement
  appliedLampSimilaritySelection: {
    lampId: number;
    measurementFileId?: number;
    previewMode: MarketMatchPreviewMode;
  } | null;
  // Original measurement data to restore when unapplying
  originalMeasurementData: MeasurementData[] | null | undefined;
  lampWireframes: LampWireframesState;
  isOptimizationStarting: boolean;
  isCreatingVersion: boolean;
  isCancelingAutoPositioning: boolean;
  isAutoPositioningStarting: boolean;
  isMarketMatchStarting: boolean;
  /** Autosave sync indicator for the workspace navbar (Drive-style). */
  autosaveStatus: AutosaveStatus;
}

/**
 * Drive-style autosave indicator state.
 * - `saving`: there are local edits that are not yet confirmed persisted
 *   (either pending the next autosave tick or a PUT is in flight).
 * - `saved`: everything is persisted server-side.
 * - `error`: the last save attempt failed.
 */
export type AutosaveStatus = 'saved' | 'saving' | 'error';

export type TargetFunction = 'Uo' | 'Ul' | 'Uow' | 'fTI' | 'REI' | 'L' | 'E' | 'Ev' | 'Esc' | 'Ehs';
export type TargetType = 'AVERAGE' | 'MIN' | 'MAX' | 'EXACT';

export interface ExpectedValue {
  function: TargetFunction;
  type: TargetType;
  value: number;
}

export interface ActualValue {
  function: TargetFunction;
  type: TargetType;
  value: number;
}

export interface MeasurementData {
  name: string; // originalName of the layer
  uuid: string;
  normId: number;
  expectedValues: ExpectedValue[];
  actualValues: ActualValue[];
  percentage?: {
    Uo: number | null;
    Ul: number | null;
    Uow: number | null;
    fTI: number | null;
    REI: number | null;
    L: number | null;
    E: number | null;
    Ev: number | null;
    Esc: number | null;
    Ehs: number | null;
  };
  positions: string;
  measurements: string;
  ignoredPoints: string | null;
}

const DEFAULT_IES_URL = 'https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/models/ies/street.ies';

const initialState: WorkspaceState = {
  showGrid: true,
  showCompass: true,
  showReferenceImage: true,
  efficacy: 100,
  maintenanceFactor: 80,
  satisfaction: 90,
  measurementGridSpacing: 0.5,
  frontendMeasurementData: null,
  measurementSource: 'ml',
  frontendMeasurementCalculating: false,
  showIESModels: false,
  showMeasurementPoints: true,
  showNearestLampDistancesWhileMoving: true,
  showXYZLines: false,
  xLines: false,
  yLines: false,
  zLines: true,
  activeViewModes: {
    object: true,
    measurement: false,
    render: false,
    realTimeRender: false,
  },
  useOriginalTextures: false,
  showMeasurements: false,
  hasCustomIesProfile: true,
  customIesProfileUrl: DEFAULT_IES_URL,
  selectedObject: null,
  transformMode: 'translate',
  sidebarOpen: true,
  hoveredLayer: null,
  selectedLayer: null,
  isLampCreationMode: false,
  lamps: [],
  uploadedModels: [],
  modelLayers: [],
  meshGroups: [],
  expandedSections: {
    layers: true,
    lights: false,
    results: false,
    history: false,
    settings: false,
    structure: true,
    photometries: true,
    measurement: true,
    marketMatches: false,
    measurementSurfaces: false,
  },
  project: null,
  cameraInitialized: false,
  desiredLuminaireHeight: 8.0,
  maxApexAngleDegrees: DEFAULT_MAX_APEX_ANGLE_DEGREES,
  fileDownloadProgress: [],
  isLoadingFiles: false,
  isOrthographicView: false,
  measurementVisualizationType: null,
  measurementFilterRange: null,
  showAutoPositionSidebar: false,
  // Measurement tool initial state
  isMeasurementMode: false,
  measurementPoints: [],
  measuredDistance: null,
  segmentDistances: [],
  activeToolbarTool: 'selection',
  measurementHoverPoint: null,
  // Lamp-to-Lamp distance initial state
  isLampDistanceMode: false,
  lampDistanceSelectedLamps: [],
  lampDistancePoints: [],
  lampDistanceMeasured: null,
  // Undo/Redo history initial state
  lampHistory: initialLampHistoryState,
  // Target camera position initial state
  targetCameraPosition: null,
  targetCameraLookAt: null,
  // Lamp similarity details initial state
  lampSimilarityDetails: [],
  isLoadingLampSimilarityDetails: false,
  // Applied lamp similarity measurement initial state
  appliedLampSimilaritySelection: null,
  // Original measurement data initial state
  originalMeasurementData: null,
  lampWireframes: initialLampWireframesState,
  isOptimizationStarting: false,
  isCreatingVersion: false,
  isCancelingAutoPositioning: false,
  isAutoPositioningStarting: false,
  isMarketMatchStarting: false,
  autosaveStatus: 'saved',
};

export const workspaceReducer = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    triggerClearWorkspaceState: (_state) => {},
    doClearWorkspaceState: () => initialState,
    loadProject: (_state, _action: PayloadAction<number>) => {},
    updateProjectVersion: (
      state,
      _action: PayloadAction<{ projectId: number; versionId: number; optimisticLockKey: number }>,
    ) => {
      state.autosaveStatus = 'saving';
    },
    updateProjectVersionSucceeded: (state, _action: PayloadAction<{ versionId: number }>) => {
      state.autosaveStatus = 'saved';
    },
    updateProjectVersionFailed: (state, _action: PayloadAction<{ versionId: number }>) => {
      state.autosaveStatus = 'error';
    },
    setAutosaveStatus: (state, action: PayloadAction<AutosaveStatus>) => {
      state.autosaveStatus = action.payload;
    },
    startPeriodicChecking: (state) => {
      state.autosaveStatus = 'saved';
    },
    stopPeriodicChecking: (_state) => {},
    switchActiveVersion: (_state, _action: PayloadAction<{ projectId: number; versionId: number }>) => {},
    createEmptyVersion: (state, _action: PayloadAction<{ projectId: number; sourceVersionId: number }>) => {
      state.isCreatingVersion = true;
    },
    duplicateVersion: (state, _action: PayloadAction<{ projectId: number; sourceVersionId: number }>) => {
      state.isCreatingVersion = true;
    },
    createVersionFromMarketMatch: (
      state,
      _action: PayloadAction<{
        projectId: number;
        sourceVersionId: number;
        lampId: number;
        measurementFileId?: number;
      }>,
    ) => {
      state.isCreatingVersion = true;
    },
    finishCreateVersion: (state) => {
      state.isCreatingVersion = false;
    },
    deleteVersion: (_state, _action: PayloadAction<{ projectId: number; versionId: number }>) => {},
    renameVersion: (_state, _action: PayloadAction<{ projectId: number; versionId: number; newName: string }>) => {},
    loadMeasurementFile: (_state, _action: PayloadAction<number>) => {},
    loadWireframeFiles: (_state, _action: PayloadAction) => {},
    loadBakedModelFile: (_state, _action: PayloadAction) => {},
    setProject: (state, action: PayloadAction<Project>) => {
      const previousActiveVersionId = state.project?.activeVersionId ?? null;
      const nextActiveVersionId = action.payload.activeVersionId ?? null;

      state.project = action.payload;

      // Prevent cross-version stale market-match detail payload from being reused
      // when versions are switched quickly or share same lamp IDs.
      if (previousActiveVersionId !== nextActiveVersionId) {
        state.lampSimilarityDetails = [];
        state.isLoadingLampSimilarityDetails = false;
      }
    },
    updateActiveVersion: (state, action: PayloadAction<Version>) => {
      state.isOptimizationStarting = false;
      if (!state.project || !state.project.versions) return;
      state.project.versions = state.project.versions.map((v) =>
        v.id === action.payload.id ? { ...v, ...action.payload } : v,
      );
    },
    setShowGrid: (state, action: PayloadAction<boolean>) => {
      state.showGrid = action.payload;
    },
    setShowCompass: (state, action: PayloadAction<boolean>) => {
      state.showCompass = action.payload;
    },
    setShowReferenceImage: (state, action: PayloadAction<boolean>) => {
      state.showReferenceImage = action.payload;
    },
    setOrthographicView: (state, action: PayloadAction<boolean>) => {
      state.isOrthographicView = action.payload;
    },
    setUseOriginalTextures: (state, action: PayloadAction<boolean>) => {
      state.useOriginalTextures = action.payload;
    },
    setShowIESModels: (state, action: PayloadAction<boolean>) => {
      state.showIESModels = action.payload;
    },
    setShowMeasurementPoints: (state, action: PayloadAction<boolean>) => {
      state.showMeasurementPoints = action.payload;
    },
    setShowNearestLampDistancesWhileMoving: (state, action: PayloadAction<boolean>) => {
      state.showNearestLampDistancesWhileMoving = action.payload;
    },
    setShowXYZLines: (state, action: PayloadAction<boolean>) => {
      state.showXYZLines = action.payload;
    },
    setXLines: (state, action: PayloadAction<boolean>) => {
      state.xLines = action.payload;
    },
    setYLines: (state, action: PayloadAction<boolean>) => {
      state.yLines = action.payload;
    },
    setZLines: (state, action: PayloadAction<boolean>) => {
      state.zLines = action.payload;
    },
    startTamanOptimization: (state) => {
      state.isOptimizationStarting = true;
    },
    finishTamanOptimization: (state) => {
      state.isOptimizationStarting = false;
    },
    setMeasurementGridSpacing: (state, action: PayloadAction<number>) => {
      state.measurementGridSpacing = action.payload;
    },
    setFrontendMeasurementData: (state, action: PayloadAction<MeasurementData[] | null>) => {
      state.frontendMeasurementData = action.payload;
    },
    setMeasurementSource: (state, action: PayloadAction<'ml' | 'frontend'>) => {
      state.measurementSource = action.payload;
    },
    setFrontendMeasurementCalculating: (state, action: PayloadAction<boolean>) => {
      state.frontendMeasurementCalculating = action.payload;
    },
    startAutoPositioning: (state) => {
      state.isAutoPositioningStarting = true;
    },
    finishAutoPositioning: (state) => {
      state.isAutoPositioningStarting = false;
    },
    cancelAutoPositioning: (state) => {
      state.isCancelingAutoPositioning = true;
    },
    finishCancelAutoPositioning: (state) => {
      state.isCancelingAutoPositioning = false;
    },
    startLampSimilarity: (
      state,
      _action: PayloadAction<{ projectId: number; versionId: number; filters?: MarketMatchPrefilterRequest }>,
    ) => {
      state.isMarketMatchStarting = true;
    },
    finishLampSimilarity: (state) => {
      state.isMarketMatchStarting = false;
    },
    loadLampSimilarityDetails: (state) => {
      state.isLoadingLampSimilarityDetails = true;
    },
    setLampSimilarityDetails: (state, action: PayloadAction<LampSimilarityDetail[]>) => {
      state.lampSimilarityDetails = action.payload;
    },
    setLoadingLampSimilarityDetails: (state, action: PayloadAction<boolean>) => {
      state.isLoadingLampSimilarityDetails = action.payload;
    },
    applyLampSimilarityMeasurement: (
      state,
      action: PayloadAction<{ lampId: number; measurementFileId?: number; previewMode: MarketMatchPreviewMode }>,
    ) => {
      const { lampId, measurementFileId, previewMode } = action.payload;
      const isSameSelection =
        state.appliedLampSimilaritySelection?.lampId === lampId &&
        state.appliedLampSimilaritySelection?.measurementFileId === measurementFileId;
      // If clicking on already applied lamp, unapply it
      if (isSameSelection) {
        const wasFrontendPreview = state.appliedLampSimilaritySelection?.previewMode === 'frontend';
        state.appliedLampSimilaritySelection = null;
        state.lampWireframes.thirdPartyWireframes = null;
        // Restore original measurement data from version
        if (state.project?.activeVersionId) {
          const version = state.project.versions.find((v) => v.id === state.project?.activeVersionId);
          if (version) {
            version.measurementData = state.originalMeasurementData;
          }
        }
        state.originalMeasurementData = null;
        if (wasFrontendPreview) {
          state.frontendMeasurementData = null;
        }
        return;
      }
      // Save original measurement data before first apply (ML preview swaps version data).
      if (previewMode === 'ml' && state.appliedLampSimilaritySelection === null && state.project?.activeVersionId) {
        const version = state.project.versions.find((v) => v.id === state.project?.activeVersionId);
        if (version) {
          state.originalMeasurementData = version.measurementData;
        }
      }
      state.appliedLampSimilaritySelection = { lampId, measurementFileId, previewMode };
      state.measurementSource = previewMode === 'ml' ? 'ml' : 'frontend';
      // Wireframes will be set by saga after IES download/generation
      state.lampWireframes.thirdPartyWireframes = null;
      if (previewMode === 'ml' && state.project?.activeVersionId) {
        const version = state.project.versions.find((v) => v.id === state.project?.activeVersionId);
        const detail = state.lampSimilarityDetails.find(
          (d) => d.lampId === lampId && d.measurementFileId === measurementFileId,
        );
        if (version && detail?.measurementData && detail.measurementData.length > 0) {
          version.measurementData = detail.measurementData;
        }
      }
    },
    setTamanWireframes: (state, action: PayloadAction<{ versionId: number; entries: LampWireframeTamanEntry[] }>) => {
      state.lampWireframes.versionId = action.payload.versionId;
      state.lampWireframes.tamanWireframes = action.payload.entries;
    },
    setThirdPartyWireframes: (state, action: PayloadAction<LampWireframeThirdPartyEntry[] | null>) => {
      state.lampWireframes.thirdPartyWireframes = action.payload;
    },
    /** Patches only the rotationRadians of one entry — no geometry rebuild needed. */
    patchThirdPartyWireframeRotation: (state, action: PayloadAction<{ lampUuid: string; rotationRadians: number }>) => {
      const entry = state.lampWireframes.thirdPartyWireframes?.find((e) => e.lampUuid === action.payload.lampUuid);
      if (entry) {
        entry.rotationRadians = action.payload.rotationRadians;
      }
    },
    clearLampWireframes: () => {
      /* Saga handles: revoke URLs, then puts doClearLampWireframes */
    },
    doClearLampWireframes: (state) => {
      state.lampWireframes = initialLampWireframesState;
    },
    clearThirdPartyLampSimilarity: () => {
      /* Saga handles: revoke URLs, then puts doClearThirdPartyLampSimilarity */
    },
    doClearThirdPartyLampSimilarity: (state) => {
      // Cleanup action used during version switching; measurement data for the new
      // version is loaded by switch/load flows.
      state.appliedLampSimilaritySelection = null;
      state.lampWireframes.thirdPartyWireframes = null;
      state.originalMeasurementData = null;
    },
    setActiveViewMode: (state, action: PayloadAction<keyof ViewModes>) => {
      const newModes = Object.keys(state.activeViewModes).reduce((acc, key) => {
        acc[key as keyof ViewModes] = false;
        return acc;
      }, {} as ViewModes);
      newModes[action.payload] = true;
      state.activeViewModes = newModes;

      if (action.payload === 'measurement' && !state.showMeasurements) {
        state.showMeasurements = true;
        // Deselect layer/lamp so selection outlines don't interfere with measurement view
        state.selectedObject = null;
        state.lamps = state.lamps.map((lamp) => ({ ...lamp, isSelected: false }));
      } else if (action.payload !== 'measurement' && state.showMeasurements) {
        state.showMeasurements = false;
      }
    },
    setMeasurementVisualizationType: (state, action: PayloadAction<MeasurementVisualizationType>) => {
      state.measurementVisualizationType = action.payload;
      // Value range is mode-specific (lx vs cd/m²), so any active filter is
      // meaningless after a mode change.
      state.measurementFilterRange = null;
    },
    setMeasurementFilterRange: (state, action: PayloadAction<{ min: number | null; max: number | null } | null>) => {
      state.measurementFilterRange = action.payload;
    },
    toggleMeasurements: (state) => {
      state.showMeasurements = !state.showMeasurements;
    },
    uploadIesProfile: (state, action: PayloadAction<string>) => {
      state.hasCustomIesProfile = true;
      state.customIesProfileUrl = action.payload;
    },
    resetToDefaultIesProfile: (state) => {
      state.hasCustomIesProfile = true;
      state.customIesProfileUrl = DEFAULT_IES_URL;
    },
    setSelectedObject: (state, action: PayloadAction<string | null>) => {
      state.selectedObject = action.payload;
    },
    setTransformMode: (state, action: PayloadAction<'translate' | 'rotate' | 'scale'>) => {
      state.transformMode = action.payload;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    setShowAutoPositionSidebar: (state, action: PayloadAction<boolean>) => {
      state.showAutoPositionSidebar = action.payload;
    },
    // Measurement tool actions
    setMeasurementMode: (state, action: PayloadAction<boolean>) => {
      state.isMeasurementMode = action.payload;
      if (!action.payload) {
        // Reset measurement data when exiting measurement mode
        state.measurementPoints = [];
        state.measuredDistance = null;
        state.segmentDistances = [];
      }
    },
    addMeasurementPoint: (state, action: PayloadAction<[number, number, number]>) => {
      state.measurementPoints.push(action.payload);

      // Recalculate all segment distances and total
      if (state.measurementPoints.length >= 2) {
        state.segmentDistances = [];
        let total = 0;
        for (let i = 1; i < state.measurementPoints.length; i++) {
          const p1 = state.measurementPoints[i - 1];
          const p2 = state.measurementPoints[i];
          const d = Math.sqrt((p2[0] - p1[0]) ** 2 + (p2[1] - p1[1]) ** 2 + (p2[2] - p1[2]) ** 2);
          state.segmentDistances.push(d);
          total += d;
        }
        state.measuredDistance = total;
      }
    },
    clearMeasurementPoints: (state) => {
      state.measurementPoints = [];
      state.measuredDistance = null;
      state.segmentDistances = [];
    },
    setActiveToolbarTool: (
      state,
      action: PayloadAction<'selection' | 'target' | 'measurement' | 'lampDistance' | null>,
    ) => {
      state.activeToolbarTool = action.payload;
      // Enable/disable measurement mode based on tool selection
      state.isMeasurementMode = action.payload === 'measurement';
      state.isLampDistanceMode = action.payload === 'lampDistance';
      if (action.payload !== 'measurement') {
        state.measurementPoints = [];
        state.measuredDistance = null;
        state.segmentDistances = [];
        state.measurementHoverPoint = null;
      }
      if (action.payload !== 'lampDistance') {
        state.lampDistanceSelectedLamps = [];
        state.lampDistancePoints = [];
        state.lampDistanceMeasured = null;
      }
    },
    setMeasurementHoverPoint: (state, action: PayloadAction<[number, number, number] | null>) => {
      state.measurementHoverPoint = action.payload;
    },
    // Lamp-to-Lamp distance actions
    addLampDistanceSelection: (
      state,
      action: PayloadAction<{ lampId: string; position: [number, number, number] }>,
    ) => {
      // If 2 lamps are already selected (measurement complete), clicking a new lamp
      // automatically resets and starts a new measurement with the clicked lamp as first point
      if (state.lampDistanceSelectedLamps.length >= 2) {
        state.lampDistanceSelectedLamps = [action.payload.lampId];
        state.lampDistancePoints = [action.payload.position];
        state.lampDistanceMeasured = null;
        return;
      }

      // Add lamp to selection (first or second)
      state.lampDistanceSelectedLamps.push(action.payload.lampId);
      state.lampDistancePoints.push(action.payload.position);

      // Calculate distance when 2 lamps are selected
      if (state.lampDistancePoints.length === 2) {
        const [p1, p2] = state.lampDistancePoints;
        const dx = p2[0] - p1[0];
        const dy = p2[1] - p1[1];
        const dz = p2[2] - p1[2];
        state.lampDistanceMeasured = Math.sqrt(dx * dx + dy * dy + dz * dz);
      }
    },
    clearLampDistanceSelection: (state) => {
      state.lampDistanceSelectedLamps = [];
      state.lampDistancePoints = [];
      state.lampDistanceMeasured = null;
    },
    setTargetCameraPosition: (
      state,
      action: PayloadAction<{ position: [number, number, number]; lookAt: [number, number, number] } | null>,
    ) => {
      if (action.payload) {
        state.targetCameraPosition = action.payload.position;
        state.targetCameraLookAt = action.payload.lookAt;
      } else {
        state.targetCameraPosition = null;
        state.targetCameraLookAt = null;
      }
    },
    setHoveredLayer: (state, action: PayloadAction<string | null>) => {
      state.hoveredLayer = action.payload;
    },
    setSelectedLayer: (state, action: PayloadAction<string | null>) => {
      state.selectedLayer = action.payload;
    },
    setLampCreationMode: (state, action: PayloadAction<boolean>) => {
      state.isLampCreationMode = action.payload;
    },
    // Internal lamp operations without history tracking (used by sagas for version switching)
    addLampsInternal: (state, action: PayloadAction<LampSeed[]>) => {
      state.lamps.push(...action.payload.map(createLampFromSeed));
    },
    removeLampInternal: (state, action: PayloadAction<string>) => {
      const lampToRemove = state.lamps.find((lamp) => lamp.id === action.payload);
      if (lampToRemove) {
        state.lampWireframes.thirdPartyWireframes = removeThirdPartyWireframeForLamp(
          state.lampWireframes.thirdPartyWireframes,
          lampToRemove.uuid,
        );
      }
      state.lamps = state.lamps.filter((lamp) => lamp.id !== action.payload);
    },
    // Lamp operations with history tracking (used by UI)
    addLamp: (state, action: PayloadAction<LampSeed>) => {
      const newLamp = createLampFromSeed(action.payload);
      state.lamps.push(newLamp);
      // Add to undo stack
      state.lampHistory.undoStack.push({
        type: 'ADD_LAMP',
        data: { lamp: newLamp },
      });
      // Clear redo stack on new action
      state.lampHistory.redoStack = [];
    },
    addLamps: (state, action: PayloadAction<LampSeed[]>) => {
      const newLamps: Lamp[] = action.payload.map(createLampFromSeed);
      state.lamps.push(...newLamps);
      // Add to undo stack
      state.lampHistory.undoStack.push({
        type: 'ADD_LAMPS',
        data: { lamps: newLamps },
      });
      // Clear redo stack on new action
      state.lampHistory.redoStack = [];
    },
    removeLamp: (state, action: PayloadAction<string>) => {
      const lampToRemove = state.lamps.find((lamp) => lamp.id === action.payload);
      if (lampToRemove) {
        // Clean up stale wireframe entry so a new lamp can't inherit it.
        state.lampWireframes.thirdPartyWireframes = removeThirdPartyWireframeForLamp(
          state.lampWireframes.thirdPartyWireframes,
          lampToRemove.uuid,
        );
        // Store the lamp for undo
        state.lampHistory.undoStack.push({
          type: 'REMOVE_LAMP',
          data: { lamp: { ...lampToRemove } },
        });
        // Clear redo stack on new action
        state.lampHistory.redoStack = [];
        // Remove the lamp
        state.lamps = state.lamps.filter((lamp) => lamp.id !== action.payload);
      }
    },
    // Several luminaires at once (e.g. "Delete selected"); one undo step brings them all back.
    removeLamps: (state, action: PayloadAction<string[]>) => {
      const ids = new Set(action.payload);
      const removed = state.lamps.filter((lamp) => ids.has(lamp.id));
      if (removed.length === 0) return;
      state.lampWireframes.thirdPartyWireframes = removeThirdPartyWireframesForLamps(
        state.lampWireframes.thirdPartyWireframes,
        removed.map((l) => l.uuid),
      );
      state.lampHistory.undoStack.push({ type: 'REMOVE_LAMPS', data: { lamps: removed.map((l) => ({ ...l })) } });
      state.lampHistory.redoStack = [];
      state.lamps = state.lamps.filter((lamp) => !ids.has(lamp.id));
    },
    updateLampPosition: (
      state,
      action: PayloadAction<{ id: string; position: [number, number, number]; oldPosition?: [number, number, number] }>,
    ) => {
      const lamp = state.lamps.find((l) => l.id === action.payload.id);
      if (lamp) {
        // Only add to history if oldPosition is provided (indicates a completed move action)
        if (action.payload.oldPosition) {
          state.lampHistory.undoStack.push({
            type: 'MOVE_LAMP',
            data: {
              lampId: action.payload.id,
              oldPosition: action.payload.oldPosition,
              newPosition: action.payload.position,
            },
          });
          // Clear redo stack on new action
          state.lampHistory.redoStack = [];
        }
        // Update position
        lamp.position = action.payload.position;
      }
    },
    // Undo action
    undoLampAction: (state) => {
      const command = state.lampHistory.undoStack.pop();
      if (!command) return;

      switch (command.type) {
        case 'ADD_LAMP':
          // Undo add: remove the lamp (clean up its wireframe entry too)
          if (command.data.lamp) {
            state.lampWireframes.thirdPartyWireframes = removeThirdPartyWireframeForLamp(
              state.lampWireframes.thirdPartyWireframes,
              command.data.lamp.uuid,
            );
            state.lamps = state.lamps.filter((lamp) => lamp.id !== command.data.lamp!.id);
          }
          break;
        case 'ADD_LAMPS':
          // Undo add multiple: remove all the lamps (clean up wireframe entries too)
          if (command.data.lamps) {
            state.lampWireframes.thirdPartyWireframes = removeThirdPartyWireframesForLamps(
              state.lampWireframes.thirdPartyWireframes,
              command.data.lamps.map((l: Lamp) => l.uuid),
            );
            const lampIds = command.data.lamps.map((l: Lamp) => l.id);
            state.lamps = state.lamps.filter((lamp) => !lampIds.includes(lamp.id));
          }
          break;
        case 'REMOVE_LAMP':
          // Undo remove: add the lamp back
          if (command.data.lamp) {
            state.lamps.push(command.data.lamp);
          }
          break;
        case 'REMOVE_LAMPS':
          // Undo remove multiple: add them all back
          if (command.data.lamps) {
            state.lamps.push(...command.data.lamps);
          }
          break;
        case 'MOVE_LAMP':
          // Undo move: restore old position
          if (command.data.lampId && command.data.oldPosition) {
            const lamp = state.lamps.find((l) => l.id === command.data.lampId);
            if (lamp) {
              lamp.position = command.data.oldPosition;
            }
          }
          break;
      }

      // Push to redo stack
      state.lampHistory.redoStack.push(command);
    },
    // Redo action
    redoLampAction: (state) => {
      const command = state.lampHistory.redoStack.pop();
      if (!command) return;

      switch (command.type) {
        case 'ADD_LAMP':
          // Redo add: add the lamp back
          if (command.data.lamp) {
            state.lamps.push(command.data.lamp);
          }
          break;
        case 'ADD_LAMPS':
          // Redo add multiple: add all the lamps back
          if (command.data.lamps) {
            state.lamps.push(...command.data.lamps);
          }
          break;
        case 'REMOVE_LAMP':
          // Redo remove: remove the lamp again (clean up wireframe entry too)
          if (command.data.lamp) {
            state.lampWireframes.thirdPartyWireframes = removeThirdPartyWireframeForLamp(
              state.lampWireframes.thirdPartyWireframes,
              command.data.lamp.uuid,
            );
            state.lamps = state.lamps.filter((lamp) => lamp.id !== command.data.lamp!.id);
          }
          break;
        case 'REMOVE_LAMPS':
          // Redo remove multiple: remove them all again (clean up wireframe entries too)
          if (command.data.lamps) {
            state.lampWireframes.thirdPartyWireframes = removeThirdPartyWireframesForLamps(
              state.lampWireframes.thirdPartyWireframes,
              command.data.lamps.map((l: Lamp) => l.uuid),
            );
            const lampIds = command.data.lamps.map((l: Lamp) => l.id);
            state.lamps = state.lamps.filter((lamp) => !lampIds.includes(lamp.id));
          }
          break;
        case 'MOVE_LAMP':
          // Redo move: apply new position
          if (command.data.lampId && command.data.newPosition) {
            const lamp = state.lamps.find((l) => l.id === command.data.lampId);
            if (lamp) {
              lamp.position = command.data.newPosition;
            }
          }
          break;
      }

      // Push back to undo stack
      state.lampHistory.undoStack.push(command);
    },
    // Reset history (called on version switch)
    resetLampHistory: (state) => {
      state.lampHistory = initialLampHistoryState;
    },
    updateLampDistance: (state, action: PayloadAction<{ id: string; distance: number }>) => {
      const lamp = state.lamps.find((l) => l.id === action.payload.id);
      if (lamp) {
        lamp.distanceToGround = action.payload.distance;
      }
    },
    updateLampName: (state, action: PayloadAction<{ id: string; name: string }>) => {
      const lamp = state.lamps.find((l) => l.id === action.payload.id);
      if (lamp) {
        lamp.name = action.payload.name;
      }
    },
    /** Assigns (or swaps) the catalogue product/variant of a luminaire (market-matched versions). */
    updateLampProductVariant: (
      state,
      action: PayloadAction<{
        id: string;
        productId: number;
        variantId: number;
        seriesId: number;
        manufacturerId: number;
      }>,
    ) => {
      const lamp = state.lamps.find((l) => l.id === action.payload.id);
      if (lamp) {
        lamp.productId = action.payload.productId;
        lamp.variantId = action.payload.variantId;
        lamp.seriesId = action.payload.seriesId;
        lamp.manufacturerId = action.payload.manufacturerId;
        // Keep existing rotationRadians — user may have set a deliberate orientation.
        // Frontend measurements are invalidated because the photometry changed.
        state.frontendMeasurementData = null;
      }
    },
    /** Updates the rotation (in radians) of a catalogue-assigned luminaire without changing its product. */
    updateLampRotationRadians: (state, action: PayloadAction<{ id: string; rotationRadians: number }>) => {
      const lamp = state.lamps.find((l) => l.id === action.payload.id);
      if (lamp) {
        lamp.rotationRadians = action.payload.rotationRadians;
      }
    },
    /** Assigns a market-match variant group to a luminaire (used only during market matching). */
    updateLampMarketMatchGroup: (
      state,
      action: PayloadAction<{ id: string; marketMatchGroup: string | undefined }>,
    ) => {
      const lamp = state.lamps.find((l) => l.id === action.payload.id);
      if (lamp) {
        const group = action.payload.marketMatchGroup?.trim();
        lamp.marketMatchGroup = group || undefined;
      }
    },
    /** Removes a market-match group from every luminaire that uses it. */
    deleteMarketMatchGroup: (state, action: PayloadAction<{ groupName: string }>) => {
      const groupName = action.payload.groupName.trim();
      if (!groupName) return;
      for (const lamp of state.lamps) {
        if (lamp.marketMatchGroup === groupName) {
          lamp.marketMatchGroup = undefined;
        }
      }
    },
    selectLamp: (state, action: PayloadAction<string>) => {
      state.lamps = state.lamps.map((lamp) => ({
        ...lamp,
        isSelected: lamp.id === action.payload,
      }));
      // state.selectedLayer = null;
    },
    addModel: (state, action: PayloadAction<ModelFile>) => {
      state.uploadedModels.push(action.payload);
    },
    removeModel: (state, action: PayloadAction<string>) => {
      state.uploadedModels = state.uploadedModels.filter((model) => model.name !== action.payload);
    },
    setModelLayers: (state, action: PayloadAction<Partial<ModelLayer>[]>) => {
      state.modelLayers = action.payload.map((layer) => ({
        ...layer,
        threeJsName: layer.threeJsName!,
        originalName: layer.originalName!,
        displayName: layer.displayName || layer.originalName!,
        color: layer.color, // Don't assign default color, let it be undefined
        uuid: layer.uuid || crypto.randomUUID(),
        groupId: layer.groupId,
        modelName: layer.modelName || 'Unnamed Model',
        visible: layer.visible !== undefined ? layer.visible : true,
        isIlluminated: layer.isIlluminated !== undefined ? layer.isIlluminated : false,
        canHoldLuminaire: layer.canHoldLuminaire !== undefined ? layer.canHoldLuminaire : false,
        layerType: layer.layerType || ('Unspecified' as LayerType),
        selectedNorm: layer.selectedNorm !== undefined ? layer.selectedNorm : null,
        customTargets: layer.customTargets ?? null,
      }));
    },
    toggleLayerVisibility: (state, action: PayloadAction<string>) => {
      const layer = state.modelLayers.find((l) => l.threeJsName === action.payload);
      if (layer) {
        layer.visible = !layer.visible;
      }
    },
    toggleLayerIllumination: (state, action: PayloadAction<string>) => {
      const layer = state.modelLayers.find((l) => l.threeJsName === action.payload);
      if (layer) {
        layer.isIlluminated = !layer.isIlluminated;
        // If illuminance is turned off, set selectedNorm to null
        if (!layer.isIlluminated) {
          layer.selectedNorm = null;
          layer.customTargets = null;
        }
      }
    },
    toggleLayerLuminaireHolder: (state, action: PayloadAction<string>) => {
      const layer = state.modelLayers.find((l) => l.threeJsName === action.payload);
      if (layer) {
        layer.canHoldLuminaire = !layer.canHoldLuminaire;
      }
    },
    updateLayerType: (state, action: PayloadAction<{ name: string; layerType: LayerType }>) => {
      const layer = state.modelLayers.find((l) => l.threeJsName === action.payload.name);
      if (layer) {
        layer.layerType = action.payload.layerType;
        if (action.payload.layerType !== 'Unspecified') {
          layer.color = LAYER_TYPES[action.payload.layerType as keyof typeof LAYER_TYPES];
        }
      }
    },
    updateLayerNorm: (state, action: PayloadAction<{ name: string; norm: string | null }>) => {
      const layer = state.modelLayers.find((l) => l.threeJsName === action.payload.name);
      if (layer) {
        layer.selectedNorm = action.payload.norm;
        layer.customTargets = null;
      }
    },
    updateLayerCustomTargets: (
      state,
      action: PayloadAction<{ name: string; targets: { function: TargetFunction; type: TargetType; value: number }[] }>,
    ) => {
      const layer = state.modelLayers.find((l) => l.threeJsName === action.payload.name);
      if (layer) {
        if (!action.payload.targets.length) {
          layer.customTargets = null;
          if (layer.selectedNorm === LAYER_NORM_CUSTOM) layer.selectedNorm = LAYER_NORM_NONE;
          return;
        }
        layer.customTargets = action.payload.targets;
        layer.selectedNorm = LAYER_NORM_CUSTOM;
        layer.isIlluminated = true;
      }
    },
    updateLayerDisplayName: (state, action: PayloadAction<{ uuid: string; displayName: string }>) => {
      const layer = state.modelLayers.find((l) => l.uuid === action.payload.uuid);
      if (layer) {
        layer.displayName = action.payload.displayName;
      }
    },
    updateLayerColor: (state, action: PayloadAction<{ uuid: string; color: string }>) => {
      const layer = state.modelLayers.find((l) => l.uuid === action.payload.uuid);
      if (layer) {
        layer.color = action.payload.color;
      }
    },
    updateLayerGroupId: (state, action: PayloadAction<{ uuid: string; groupId: string | null }>) => {
      const layer = state.modelLayers.find((l) => l.uuid === action.payload.uuid);
      if (layer) {
        layer.groupId = action.payload.groupId;
      }
    },
    setMeshGroups: (state, action: PayloadAction<MeshGroup[]>) => {
      state.meshGroups = action.payload;
    },
    addMeshGroup: (state, action: PayloadAction<{ group: MeshGroup; parentGroupId?: string | null }>) => {
      const { group, parentGroupId } = action.payload;
      if (!parentGroupId) {
        state.meshGroups.push(group);
      } else {
        const addToParent = (groups: MeshGroup[]): boolean => {
          for (const g of groups) {
            if (g.uuid === parentGroupId) {
              g.subgroups.push(group);
              return true;
            }
            if (addToParent(g.subgroups)) return true;
          }
          return false;
        };
        addToParent(state.meshGroups);
      }
    },
    renameMeshGroup: (state, action: PayloadAction<{ uuid: string; name: string }>) => {
      const rename = (groups: MeshGroup[]): boolean => {
        for (const g of groups) {
          if (g.uuid === action.payload.uuid) {
            g.name = action.payload.name;
            return true;
          }
          if (rename(g.subgroups)) return true;
        }
        return false;
      };
      rename(state.meshGroups);
    },
    deleteMeshGroup: (state, action: PayloadAction<string>) => {
      // Collect all group UUIDs being deleted (including subgroups)
      const collectIds = (groups: MeshGroup[]): string[] => {
        const ids: string[] = [];
        for (const g of groups) {
          ids.push(g.uuid);
          ids.push(...collectIds(g.subgroups));
        }
        return ids;
      };
      const findGroup = (groups: MeshGroup[]): MeshGroup | null => {
        for (const g of groups) {
          if (g.uuid === action.payload) return g;
          const found = findGroup(g.subgroups);
          if (found) return found;
        }
        return null;
      };
      const group = findGroup(state.meshGroups);
      if (group) {
        const deletedIds = new Set(collectIds([group]));
        // Move meshes in deleted groups to root (null groupId)
        state.modelLayers.forEach((layer) => {
          if (layer.groupId && deletedIds.has(layer.groupId)) {
            layer.groupId = null;
          }
        });
      }
      // Remove the group from the tree
      const removeGroup = (groups: MeshGroup[]): MeshGroup[] => {
        return groups.filter((g) => {
          if (g.uuid === action.payload) return false;
          g.subgroups = removeGroup(g.subgroups);
          return true;
        });
      };
      state.meshGroups = removeGroup(state.meshGroups);
    },
    toggleSection: (state, action: PayloadAction<string>) => {
      state.expandedSections[action.payload] = !state.expandedSections[action.payload];
    },
    openSection: (state, action: PayloadAction<string>) => {
      state.expandedSections[action.payload] = true;
    },
    setExpandedSections: (state, action: PayloadAction<Partial<Record<string, boolean>>>) => {
      // Apply multiple section open/close flags in a single dispatch — used
      // by the sidebar's per-version init so layers/lights/results land in
      // the right starting state without three separate dispatches racing.
      for (const [key, value] of Object.entries(action.payload)) {
        if (value !== undefined) state.expandedSections[key] = value;
      }
    },
    handleLayerClick: (state, action: PayloadAction<{ threeJsName: string; originalName: string }>) => {
      const { threeJsName } = action.payload;

      // Deselect any lamp
      state.lamps = state.lamps.map((lamp) => ({
        ...lamp,
        isSelected: false,
      }));

      // Clear selected object
      state.selectedObject = null;

      // Toggle layer selection
      if (state.selectedLayer === threeJsName) {
        state.selectedLayer = null;
        state.hoveredLayer = null;
      } else {
        state.selectedLayer = threeJsName;
        state.hoveredLayer = threeJsName;
      }
    },
    setCameraInitialized: (state, action: PayloadAction<boolean>) => {
      state.cameraInitialized = action.payload;
    },
    setDesiredLuminaireHeight: (state, action: PayloadAction<number>) => {
      state.desiredLuminaireHeight = action.payload;
    },
    setMaxApexAngleDegrees: (state, action: PayloadAction<number>) => {
      state.maxApexAngleDegrees = clampMaxApexAngle(action.payload);
    },
    startFileDownload: (
      state,
      action: PayloadAction<{
        fileId: string;
        fileName: string;
        fileType: FileType;
        totalSize: number;
      }>,
    ) => {
      const existingIndex = state.fileDownloadProgress.findIndex((p) => p.fileId === action.payload.fileId);
      if (existingIndex >= 0) {
        state.fileDownloadProgress[existingIndex] = {
          ...action.payload,
          downloadedSize: 0,
          isComplete: false,
        };
      } else {
        state.fileDownloadProgress.push({
          ...action.payload,
          downloadedSize: 0,
          isComplete: false,
        });
      }
      state.isLoadingFiles = true;
    },
    updateFileProgress: (state, action: PayloadAction<{ fileId: string; downloadedSize: number }>) => {
      const progress = state.fileDownloadProgress.find((p) => p.fileId === action.payload.fileId);
      if (progress) {
        progress.downloadedSize = action.payload.downloadedSize;
      }
    },
    completeFileDownload: (state, action: PayloadAction<{ fileId: string }>) => {
      const progress = state.fileDownloadProgress.find((p) => p.fileId === action.payload.fileId);
      if (progress) {
        progress.isComplete = true;
        progress.downloadedSize = progress.totalSize;
      }
      // Check if all files are complete
      const allComplete = state.fileDownloadProgress.every((p) => p.isComplete);
      if (allComplete) {
        state.isLoadingFiles = false;
      }
    },
    resetFileProgress: (state) => {
      state.fileDownloadProgress = [];
      state.isLoadingFiles = false;
    },
    downloadIesFile: (_state, _action: PayloadAction<number>) => {},
    downloadReportImages: (_state, _action: PayloadAction) => {},
    downloadBlenderProject: (_state, _action: PayloadAction<{ projectId: number; versionId: number }>) => {},
  },
});

export const {
  triggerClearWorkspaceState,
  doClearWorkspaceState,
  loadProject,
  setProject,
  updateActiveVersion,
  setMeasurementGridSpacing,
  setFrontendMeasurementData,
  setMeasurementSource,
  setFrontendMeasurementCalculating,
  setShowGrid,
  setShowCompass,
  setShowReferenceImage,
  setOrthographicView,
  setUseOriginalTextures,
  setShowIESModels,
  setShowMeasurementPoints,
  setShowNearestLampDistancesWhileMoving,
  setShowXYZLines,
  setXLines,
  setYLines,
  setZLines,
  startTamanOptimization,
  finishTamanOptimization,
  startAutoPositioning,
  finishAutoPositioning,
  cancelAutoPositioning,
  finishCancelAutoPositioning,
  finishLampSimilarity,
  startLampSimilarity,
  loadLampSimilarityDetails,
  setLampSimilarityDetails,
  setLoadingLampSimilarityDetails,
  applyLampSimilarityMeasurement,
  setTamanWireframes,
  setThirdPartyWireframes,
  patchThirdPartyWireframeRotation,
  clearLampWireframes,
  doClearLampWireframes,
  clearThirdPartyLampSimilarity,
  doClearThirdPartyLampSimilarity,
  setActiveViewMode,
  setMeasurementFilterRange,
  setMeasurementVisualizationType,
  toggleMeasurements,
  uploadIesProfile,
  resetToDefaultIesProfile,
  setSelectedObject,
  setTransformMode,
  setSidebarOpen,
  setShowAutoPositionSidebar,
  setMeasurementMode,
  addMeasurementPoint,
  clearMeasurementPoints,
  setActiveToolbarTool,
  setMeasurementHoverPoint,
  addLampDistanceSelection,
  clearLampDistanceSelection,
  setTargetCameraPosition,
  setHoveredLayer,
  setSelectedLayer,
  setLampCreationMode,
  addLampsInternal,
  removeLampInternal,
  addLamp,
  addLamps,
  removeLamp,
  removeLamps,
  updateLampPosition,
  undoLampAction,
  redoLampAction,
  resetLampHistory,
  updateLampDistance,
  updateLampName,
  updateLampProductVariant,
  updateLampRotationRadians,
  updateLampMarketMatchGroup,
  deleteMarketMatchGroup,
  selectLamp,
  addModel,
  removeModel,
  setModelLayers,
  toggleLayerVisibility,
  toggleLayerIllumination,
  toggleLayerLuminaireHolder,
  updateLayerType,
  updateLayerNorm,
  updateLayerCustomTargets,
  updateLayerDisplayName,
  updateLayerColor,
  updateLayerGroupId,
  setMeshGroups,
  addMeshGroup,
  renameMeshGroup,
  deleteMeshGroup,
  toggleSection,
  openSection,
  setExpandedSections,
  updateProjectVersion,
  updateProjectVersionSucceeded,
  updateProjectVersionFailed,
  setAutosaveStatus,
  startPeriodicChecking,
  stopPeriodicChecking,
  switchActiveVersion,
  createEmptyVersion,
  duplicateVersion,
  createVersionFromMarketMatch,
  finishCreateVersion,
  deleteVersion,
  renameVersion,
  loadMeasurementFile,
  loadWireframeFiles,
  loadBakedModelFile,
  handleLayerClick,
  setCameraInitialized,
  setDesiredLuminaireHeight,
  setMaxApexAngleDegrees,
  startFileDownload,
  updateFileProgress,
  completeFileDownload,
  resetFileProgress,
  downloadIesFile,
  downloadReportImages,
  downloadBlenderProject,
} = workspaceReducer.actions;
